'use strict';

const _ = require('underscore');
const Mn = require('backbone.marionette');
const i18n = require('i18next');
const ModalView = require('common/ui/view/modal/index');
const GroupMemberIconView = require('common/ui/view/group/member-icon');
const GroupMemberNameView = require('common/ui/view/group/member-name');
const ActionRepository = require('process/domain/repository/action/index');
const ContentService = require('data/app/service/content/index');
const Version = require('data/domain/entity/content/version');
const VersionCollection = require('data/domain/collection/content/version');
const DataRepository = require('data/domain/repository/content/index');
const StorageAPI = require('data/infra/api/storage/index');
const URLCopyView = require('data/ui/view/modal/url-copy');
const URLCopyFooterView = require('data/ui/view/modal/url-copy-footer');
const DiagramContext = require('system/domain/value/context');
const DiagramAction = require('system/domain/action/diagram');
const DiagramRepository = require('system/domain/repository/diagram');
const ElementRepository = require('system/domain/repository/element');
const ContentEditView = require('balus/ui/view/body/process/toolbar/content-edit');
const AdditionalTemplate = require('balus/ui/template/additional/index.hbs');
const ActionTemplate = require('balus/ui/template/additional/action.hbs');
const ContentVersionTemplate = require('balus/ui/template/additional/content-version.hbs');
const DiagramTemplate = require('balus/ui/template/additional/diagram.hbs');
const RecordTemplate = require('balus/ui/template/additional/record.hbs');
const RelationTemplate = require('balus/ui/template/additional/relation.hbs');
const AdditionalSelectorView = require('balus/ui/view/additional/selector');
const Comment = require('communication/domain/entity/comment');
const Key = require('common/domain/value/key');
const RelatedKeys = require('common/domain/value/related-keys');
const OpenURI = require('data/ui/view/content/open-uri');

const ActionView = Mn.View.extend({
    template: ActionTemplate,
    tagName: 'li',
    className: 'additional-item',
    regions: {
        assigneeIcon: '.action-assignee-icon'
    },

    templateContext: function() {
        const assignees = this.model.getAssignees();

        return {
            isEditable: this.getOption('isEditable'),
            path: this.model.path(),
            state: this.model.getCurrentState().toJSON(),
            assignee: assignees.length > 0 ? assignees[0].toJSON() : null,
            moreAssigneesNum: assignees.length > 1 ? assignees.length - 1 : null,
        }
    },

    onRender: function() {
        const group = this.getOption('group');
        const assignees = this.model.getAssignees();

        if(assignees.length > 0) {
            const assignee = group.getMembers({withSuspended: true}).getMemberByUser(assignees[0]);
            this.showChildView('assigneeIcon', new GroupMemberIconView({
                model: assignee
            }));
        }
    }
});

const ContentVersionView = Mn.View.extend({
    template: ContentVersionTemplate,
    regions: {
        uri: '.uri'
    },
    triggers: {
        'click .remove': 'remove:key',
        'click .add-link-comment-button': 'add:comment'
    },
    templateContext: function() {
        const version = this.model;
        const content = version.getContent();
        const contentType = version.getMetaData().getContentType();
        const isDeleted = version.isDeleted();
        const hasLaterVersion = isDeleted ? false : version.id != content.getLatestVersion().id;
        const path = isDeleted ? null : version.getContent().path();

        return {
            isDeleted: isDeleted,
            isEditable: this.getOption('isEditable'),
            showThumbnail: true,
            hasLaterVersion: hasLaterVersion,
            contentType: contentType,
            path: path
        };
    },
    onRender: function() {
        const version = this.model;
        // isDeleted の場合は uri が存在しないので、showChildView しないようにする
        if(!version.isDeleted()) {
            this.showChildView('uri', new OpenURI({
                version: version,
                project: version.isDeleted() ? null : version.getContent().getProject()
            }));
        }
    },

    serializeData: function() {
        return this.model.toJSON();
    },
});

const DiagramView = Mn.View.extend({
    template: DiagramTemplate,
    className: 'additional',
    events: {
        'click .additional-item-header-title .push-state': 'open'
    },
    triggers: {
        'click .remove': 'remove:key',
        'click .add-link-comment-button': 'add:comment'
    },
    templateContext: function() {
        return {
            isEditable: this.getOption('isEditable'),
            url: this.model.path()
        };
    },
    open: function() {
        // アクション操作イベントをトラッキングするならば、ここに仕込む
    }
});

const RecordView = Mn.View.extend({
    template: RecordTemplate,
    className: 'additional',
    triggers: {
        'click .remove': 'remove:key',
        'click .add-link-comment-button': 'add:comment'
    },
    templateContext: function() {
        return {
            isEditable: this.getOption('isEditable'),
            type: this.model.i18nType(),
            url: `#/elements/${this.model.id}`
        };
    }
});

const RelationView = Mn.View.extend({
    template: RelationTemplate,
    className: 'additional',
    triggers: {
        'click .remove': 'remove:key',
        'click .add-link-comment-button': 'add:comment'
    },
    templateContext: function() {
        return {
            isEditable: this.getOption('isEditable'),
            type: this.model.i18nType(),
            url: `#/elements/${this.model.id}`
        };
    }
});

module.exports = Mn.View.extend({
    hidePrimaryRelatedKey: false,
    template: AdditionalTemplate,
    regions: {
        actionList: '.action-list',
        contentVersionList: '.content-version-list',
        diagramList: '.diagram-list',
        recordList: '.record-list',
        relationList: '.relation-list'
    },
    ui: {
        contentAddButton: '.content-add-button'
    },
    events: {
        'click @ui.contentAddButton': 'addContent'
    },
    childViewEvents: {
        'remove:key': 'onRemoveKey',
        'add:comment': 'onAddComment'
    },
    childViewTriggers: {
        'comment:additional:add': 'comment:additional:add'
    },

    initialize: function() {
        const context = this.getOption('context');
        this._project = context.getProject();
        this._relatedKeys = this._getRelatedKeys();
        this._actions = new Backbone.Collection();
        this._contentVersions = new VersionCollection();
        this._diagrams = new Backbone.Collection();
        this._records = new Backbone.Collection();
        this._relations = new Backbone.Collection();

        const hidePrimaryRelatedKey = this.getOption('hidePrimaryRelatedKey');
        if (hidePrimaryRelatedKey) {
            this._relatedKeys = new RelatedKeys(this._relatedKeys.getKeysWithoutPrimaryKey());
        }

        this.updateRelatedKeys();

        this._relatedKeys.on('add remove', () => {
            this.trigger('update:key');
        });

        this._selectorView = new AdditionalSelectorView({
            model: this.model,
            context: context,
            isEditable: true
        });
        this._selectorView.on('add:key', keys => {
            this.addRelatedKeys(keys);
            this.onRender();
        });
    },

    templateContext: function() {
        const context = this.getOption('context');
        let showModelURLAt = false;
        let modelURLAt, modelDiffURLFrom;

        if(context instanceof DiagramContext && this.model instanceof Comment) {
            const diagram = context.getDiagram();
            showModelURLAt = true;
            modelURLAt = `${diagram.pathAt(this.model.getCreatedAt())}#/comments/${this.model.id}`
            modelDiffURLFrom = `${diagram.pathDiff(this.model.getCreatedAt())}#/comments/${this.model.id}`
        }

        return {
            isEditable: this.getOption('isEditable'),
            actions: this._relatedKeys.getActionKeys(),
            contentVersions: this._relatedKeys.getContentVersionKeys(),
            diagrams: this._relatedKeys.getDiagramKeys(),
            records: this._relatedKeys.getRecordKeys(),
            relations: this._relatedKeys.getRelationKeys(),
            showModelURLAt: showModelURLAt,
            modelURLAt: modelURLAt,
            modelDiffURLFrom: modelDiffURLFrom
        }
    },

    cleanup: function() {
        this._relatedKeys.reset([]);
        this._actions.reset([]);
        this._records.reset([]);
        this._relations.reset([]);
        this._diagrams.reset([]);
        this._contentVersions.reset([]);
    },

    updateRelatedKeys: function() {
        // commentLoader が未指定の場合にエラーにならないように、ダミーの関数を用意する
        const commentLoader = this.getOption('commentLoader') || { start: function () {}, end: function () {} };

        _.each(this._relatedKeys.getActionKeys(), key => {
            commentLoader.start();
            ActionRepository.find(this._project)
                .get(key.id)
                .then(action => {
                    if(!action.getParent()) {
                        return;
                    }
                    this._actions.add(action);
                })
                .catch(() => {
                    commentLoader.end();
                });
        });

        _.each(this._relatedKeys.getContentVersionKeys(), key => {
            commentLoader.start();
            DataRepository.find(this._project)
                .fetchContentOnce(key.parent.id)
                .then(content => {
                    return content.getVersionByID(key.id);
                })
                .catch(() => {
                    return (new Version({deleted: true}));
                })
                .then(contentVersion => {
                    this._contentVersions.add(contentVersion);
                });
        });

        _.each(this._relatedKeys.getDiagramKeys(), key => {
            commentLoader.start();
            DiagramAction.fetchDiagram(this._project.id, key.id)
                .then(() => {
                    const diagram = DiagramRepository.getDiagrams({
                        context: {
                            projectId: this._project.id
                        }
                    }).get(key.id);
                    this._diagrams.add(diagram);
                })
                .catch(() => {
                    commentLoader.end();
                });
        });

        _.each(this._relatedKeys.getRecordKeys(), key => {
            //当面 Record はダイアグラムでしか表示されないと思われるため
            //Timeline を表示する時点で fetch は完了しているはず
            //TODO: ちゃんと Record を fetch する
            commentLoader.start();
            const record = ElementRepository.getNodes({
                context: {
                    projectId: this._project.id,
                    dt: null
                }
            }).get(key.id);

            if(record) {
                this._records.add(record);
            } else {
                commentLoader.end();
            }
        });

        _.each(this._relatedKeys.getRelationKeys(), key => {
            //当面 Relation はダイアグラムでしか表示されないと思われるため
            //Timeline を表示する時点で fetch は完了しているはず
            //TODO: ちゃんと Relation を fetch する
            commentLoader.start();
            const relation = ElementRepository.getLinks({
                context: {
                    projectId: this._project.id,
                    dt: null
                }
            }).get(key.id);

            if(relation) {
                this._relations.add(relation);
            } else {
                commentLoader.end();
            }
        });
    },

    onRender: function() {
        const group = this.getOption('context').getGroup();
        this.showChildView('actionList',
            new (
                Mn.CollectionView.extend({
                    childView: ActionView,
                    childViewOptions: function() {
                        return {
                            group: group
                        }
                    },
                    onAddChild: function() {
                        this.triggerMethod('comment:additional:add');
                        const commentLoader = this.getOption('commentLoader');
                        commentLoader && commentLoader.delayedEnd();
                    }
                })
            )({
                commentLoader: this.getOption('commentLoader'),
                collection: this._actions,
                group: group
            })
        );
        this.showChildView('contentVersionList',
            new (
                Mn.CollectionView.extend({
                    childView: ContentVersionView,
                    childViewOptions: function() {
                        return {
                            isEditable: this.getOption('isEditable')
                        }
                    },
                    childViewTriggers: {
                        'remove:key': 'remove:key',
                        'add:comment': 'add:comment'
                    },
                    onRemoveKey: function(child) {
                        this.collection.remove(child.model, {silent: true})
                    },
                    onAddChild: function () {
                        this.triggerMethod('comment:additional:add');
                        const commentLoader = this.getOption('commentLoader');
                        commentLoader && commentLoader.delayedEnd();
                    }
                })
            )({
                collection: this._contentVersions,
                isEditable: this.getOption('isEditable'),
                commentLoader: this.getOption('commentLoader'),
            })
        );
        this.showChildView('diagramList',
            new (
                Mn.CollectionView.extend({
                    childView: DiagramView,
                    childViewOptions: function() {
                        return {
                            context: this.getOption('context'),
                            isEditable: this.getOption('isEditable')
                        }
                    },
                    childViewTriggers: {
                        'remove:key': 'remove:key',
                        'add:comment': 'add:comment'
                    },
                    onRemoveKey: function(child) {
                        this.collection.remove(child.model, {silent: true})
                    },
                    onAddChild: function () {
                        this.triggerMethod('comment:additional:add');
                        const commentLoader = this.getOption('commentLoader');
                        commentLoader && commentLoader.delayedEnd();
                    }
                })
            )({
                collection: this._diagrams,
                context: this.getOption('context'),
                isEditable: this.getOption('isEditable'),
                commentLoader: this.getOption('commentLoader'),
            })
        );

        this.showChildView('recordList',
            new (
                Mn.CollectionView.extend({
                    childView: RecordView,
                    childViewOptions: function() {
                        return {
                            context: this.getOption('context'),
                            isEditable: this.getOption('isEditable')
                        }
                    },
                    childViewTriggers: {
                        'remove:key': 'remove:key',
                        'add:comment': 'add:comment'
                    },
                    onRemoveKey: function(child) {
                        this.collection.remove(child.model, {silent: true})
                    },
                    onAddChild: function () {
                        this.triggerMethod('comment:additional:add');
                        const commentLoader = this.getOption('commentLoader');
                        commentLoader && commentLoader.delayedEnd();
                    }
                })
            )({
                collection: this._records,
                context: this.getOption('context'),
                isEditable: this.getOption('isEditable'),
                commentLoader: this.getOption('commentLoader'),
            })
        );

        this.showChildView('relationList',
            new (
                Mn.CollectionView.extend({
                    childView: RelationView,
                    childViewOptions: function() {
                        return {
                            context: this.getOption('context'),
                            isEditable: this.getOption('isEditable')
                        }
                    },
                    childViewTriggers: {
                        'remove:key': 'remove:key',
                        'add:comment': 'add:comment'
                    },
                    onRemoveKey: function(child) {
                        this.collection.remove(child.model, {silent: true})
                    },
                    onAddChild: function () {
                        this.triggerMethod('comment:additional:add');
                        const commentLoader = this.getOption('commentLoader');
                        commentLoader && commentLoader.delayedEnd();
                    }
                })
            )({
                collection: this._relations,
                context: this.getOption('context'),
                isEditable: this.getOption('isEditable'),
                commentLoader: this.getOption('commentLoader'),
            })
        );
    },

    _getRelatedKeys() {
        if(this.getOption('relatedKeys')) {
            return this.getOption('relatedKeys');
        } else {
            return this.model.getRelatedKeys();
        }
    },

    getRelatedKeys() {
        return this._relatedKeys.keys;
    },

    addRelatedKeys(keys) {
        this._relatedKeys.add(keys);
        this.updateRelatedKeys();
        this.triggerMethod('add:keys:comment', keys);
        this.trigger('add:relatedKey');
        this.render();
    },

    getSelectorView() {
        return this._selectorView;
    },

    onRemoveKey: function(child) {
        const id = child.model.key.get('id');
        if(id.match(/DataContentVersion/)) {
            const dataContentKey = this._relatedKeys.find((key) => {
                return key.get('id') === id.replace(/\/DataContentVersion:\d/, '');
            });
            this._relatedKeys.remove(dataContentKey, {silent: true});
        }
        this._relatedKeys.remove(child.model.key, {silent: true});
        this.triggerMethod('remove:keys:comment', [child.model.key]);
        this.trigger('remove:relatedKey');
        child.destroy();
    },

    onAddComment: function(child) {
        this.triggerMethod('add:content:comment', child);
    }
});



// WEBPACK FOOTER //
// ./balus/ui/view/additional/index.js